import {
  Alert,
  Badge,
  Button,
  Divider,
  Group,
  Loader,
  Modal,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  Textarea,
  Title,
} from "@mantine/core";
import { useCallback, useEffect, useState } from "react";

import { ApiError, api, type CampaignResponse, type InboxFile, type RunResponse } from "../lib/api";
import { PdfViewer } from "../components/PdfViewer";

export function InboxScreen() {
  const [runs, setRuns] = useState<RunResponse[] | null>(null);
  const [campaigns, setCampaigns] = useState<CampaignResponse[]>([]);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [files, setFiles] = useState<InboxFile[] | null>(null);
  const [includeClosedRuns, setIncludeClosedRuns] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reviewFile, setReviewFile] = useState<InboxFile | null>(null);

  const refresh = useCallback(async () => {
    if (!selectedRunId) return;
    try {
      const resp = await api.inboxFiles(selectedRunId, true);
      setFiles(resp.files);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : "שגיאה בטעינת הקבצים");
    }
  }, [selectedRunId]);

  // Load campaigns once so we can label runs by campaign name.
  useEffect(() => {
    api.campaignsList().then((r) => setCampaigns(r.campaigns)).catch(() => {});
  }, []);

  useEffect(() => {
    api
      .runsList(includeClosedRuns)
      .then((resp) => {
        setRuns(resp.runs);
        if (resp.runs.length > 0) {
          const stillThere =
            selectedRunId !== null && resp.runs.some((r) => r.run_id === selectedRunId);
          if (!stillThere) setSelectedRunId(resp.runs[0].run_id);
        } else {
          setSelectedRunId(null);
        }
      })
      .catch((err) => {
        setError(err instanceof ApiError ? err.detail : "שגיאה בטעינת הקמפיינים");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeClosedRuns]);

  useEffect(() => {
    if (!selectedRunId) return;
    setFiles(null);
    refresh();
    const i = window.setInterval(refresh, 5000);
    return () => window.clearInterval(i);
  }, [selectedRunId, refresh]);

  const handleDecision = async (fileId: string, decision: "approve" | "reject", reason?: string) => {
    try {
      if (decision === "approve") {
        await api.approveFile(fileId);
      } else {
        await api.rejectFile(fileId, reason ?? "");
      }
      setReviewFile(null);
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : "שגיאה בשמירת ההחלטה");
    }
  };

  if (runs === null) {
    return (
      <Stack>
        <Title order={2}>תיבת הקלט</Title>
        <Loader size="sm" />
      </Stack>
    );
  }
  if (runs.length === 0) {
    return (
      <Stack>
        <Title order={2}>תיבת הקלט</Title>
        <Text c="dimmed">עדיין לא נוצרו קמפיינים. צור קמפיין ולקוח בלשונית ניהול קמפיינים.</Text>
      </Stack>
    );
  }

  return (
    <Stack>
      <Title order={2}>תיבת הקלט</Title>
      <Text c="dimmed">קבצים שהגיעו מלקוחות ועברו דרך סוכן המשרד.</Text>

      <Paper p="md" withBorder>
        <Stack>
          <Group>
            <Select
              label="קמפיין"
              data={(() => {
                const nameById = Object.fromEntries(campaigns.map((c) => [c.campaign_id, c.name]));
                return runs.map((r) => {
                  const name = nameById[r.campaign_id] ?? r.period_label ?? r.run_id;
                  return { value: r.run_id, label: r.closed_at ? `${name} (סגור)` : name };
                });
              })()}
              value={selectedRunId}
              onChange={setSelectedRunId}
              w={260}
            />
            <Switch
              label="הצג גם קמפיינים סגורים"
              checked={includeClosedRuns}
              onChange={(e) => setIncludeClosedRuns(e.currentTarget.checked)}
              mt={26}
            />
            <Button variant="light" mt={26} onClick={refresh}>
              רענן
            </Button>
          </Group>

          {error && <Alert color="red">{error}</Alert>}

          {files === null ? (
            <Loader size="sm" />
          ) : files.length === 0 ? (
            <Text c="dimmed">אין קבצים בקמפיין זה עדיין.</Text>
          ) : (
            <Table striped highlightOnHover dir="rtl">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>לקוח</Table.Th>
                  <Table.Th>מסמך</Table.Th>
                  <Table.Th>גודל</Table.Th>
                  <Table.Th>הועלה</Table.Th>
                  <Table.Th>סטטוס</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {files.map((f) => (
                  <Table.Tr key={f.file_id}>
                    <Table.Td>{f.client_name ?? "—"}</Table.Td>
                    <Table.Td>{f.document_label ?? "ללא תווית"}</Table.Td>
                    <Table.Td>{formatBytes(f.size_bytes)}</Table.Td>
                    <Table.Td>{formatTimeAgo(f.uploaded_at)}</Table.Td>
                    <Table.Td>
                      <Stack gap={2}>
                        <StatusBadge status={f.status} />
                        {f.status === "rejected" && f.rejection_reason && (
                          <Text size="xs" c="red">
                            סיבה: {f.rejection_reason}
                          </Text>
                        )}
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      {f.status !== "archived" && (
                        <Button
                          size="xs"
                          variant="light"
                          onClick={() => setReviewFile(f)}
                        >
                          פתח לבדיקה
                        </Button>
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Stack>
      </Paper>

      {reviewFile && (
        <FileReviewModal
          file={reviewFile}
          onClose={() => setReviewFile(null)}
          onDecision={handleDecision}
        />
      )}
    </Stack>
  );
}

function FileReviewModal({
  file,
  onClose,
  onDecision,
}: {
  file: InboxFile;
  onClose: () => void;
  onDecision: (fileId: string, decision: "approve" | "reject", reason?: string) => Promise<void>;
}) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState(false);
  const [rejecting, setRejecting] = useState(file.status === "rejected");
  const [reason, setReason] = useState(file.rejection_reason ?? "");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setRejecting(file.status === "rejected");
    setReason(file.rejection_reason ?? "");
    setPreviewUrl(null);
    setPreviewError(false);
    let blobUrl: string | null = null;
    api
      .fileDownloadUrl(file.file_id)
      .then((r) => { blobUrl = r.url; setPreviewUrl(r.url); })
      .catch(() => setPreviewError(true));
    return () => {
      if (blobUrl?.startsWith("blob:")) URL.revokeObjectURL(blobUrl);
    };
  }, [file.file_id, file.status, file.rejection_reason]);

  const decide = async (decision: "approve" | "reject") => {
    if (decision === "reject" && !rejecting) {
      setRejecting(true);
      return;
    }
    if (decision === "reject" && !reason.trim()) return;
    setBusy(true);
    try {
      await onDecision(file.file_id, decision, decision === "reject" ? reason.trim() : undefined);
    } finally {
      setBusy(false);
    }
  };

  const alreadyDecided = file.status === "approved" || file.status === "rejected";

  return (
    <Modal
      opened
      onClose={onClose}
      title={`בדיקת מסמך — ${file.document_label ?? "ללא תווית"} (${file.client_name ?? ""})`}
      size="xl"
      styles={{
        content: { display: "flex", flexDirection: "column", maxHeight: "92vh" },
        header: { flexShrink: 0 },
        body: { padding: 0, flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" },
      }}
    >
      <Stack gap={0} style={{ flex: 1, minHeight: 0 }}>
        {/* PDF viewer fills all remaining height; decision panel is always visible below */}
        <div style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
          {previewUrl ? (
            <PdfViewer url={previewUrl} />
          ) : previewError ? (
            <Stack align="center" justify="center" style={{ height: "100%", background: "#f1f3f5" }}>
              <Text c="dimmed" size="sm">לא ניתן לטעון את התצוגה המקדימה</Text>
            </Stack>
          ) : (
            <Stack align="center" justify="center" style={{ height: "100%", background: "#f1f3f5" }}>
              <Loader size="sm" />
            </Stack>
          )}
        </div>

        <Divider />

        <Stack p="md" gap="sm" style={{ flexShrink: 0 }}>
          <Group justify="space-between">
            <Group gap="xs">
              <Text size="sm" fw={500}>{file.client_name}</Text>
              <Text size="sm" c="dimmed">·</Text>
              <Text size="sm" c="dimmed">{formatBytes(file.size_bytes)}</Text>
              <Text size="sm" c="dimmed">·</Text>
              <Text size="sm" c="dimmed">הועלה {formatTimeAgo(file.uploaded_at)}</Text>
            </Group>
            <StatusBadge status={file.status} />
          </Group>

          {file.status === "rejected" && file.rejection_reason && !rejecting && (
            <Alert color="red" title="נדחה בעבר">
              {file.rejection_reason}
            </Alert>
          )}

          {rejecting && (
            <Textarea
              label="סיבת הדחייה"
              description="הסיבה תוצג ללקוח בעמוד ההעלאה ותישמר לתצוגה חוזרת"
              value={reason}
              onChange={(e) => setReason(e.currentTarget.value)}
              maxLength={500}
              autosize
              minRows={3}
              placeholder='לדוגמה: "המסמך הוא לשנת 2024 — נא להעלות לשנת 2025"'
              autoFocus
            />
          )}

          <Group justify="flex-end">
            {rejecting ? (
              <>
                <Button variant="subtle" onClick={() => { setRejecting(false); setReason(file.rejection_reason ?? ""); }}>
                  ביטול
                </Button>
                <Button
                  color="red"
                  loading={busy}
                  disabled={!reason.trim()}
                  onClick={() => decide("reject")}
                >
                  שלח דחייה
                </Button>
              </>
            ) : (
              <>
                <Button variant="subtle" onClick={onClose}>סגור</Button>
                {!alreadyDecided && (
                  <>
                    <Button color="red" variant="light" onClick={() => setRejecting(true)}>
                      דחה
                    </Button>
                    <Button color="green" loading={busy} onClick={() => decide("approve")}>
                      אשר
                    </Button>
                  </>
                )}
                {file.status === "approved" && (
                  <Button color="red" variant="light" onClick={() => setRejecting(true)}>
                    בטל אישור — דחה
                  </Button>
                )}
                {file.status === "rejected" && (
                  <Button color="green" loading={busy} onClick={() => decide("approve")}>
                    אשר בכל זאת
                  </Button>
                )}
              </>
            )}
          </Group>
        </Stack>
      </Stack>
    </Modal>
  );
}

function StatusBadge({ status }: { status: InboxFile["status"] }) {
  const map: Record<InboxFile["status"], { label: string; color: string }> = {
    uploaded: { label: "ממתין למשיכה", color: "gray" },
    pulled: { label: "במשיכה", color: "yellow" },
    acked: { label: "נמשך למשרד", color: "green" },
    pending_review: { label: "ממתין לבדיקה", color: "yellow" },
    approved: { label: "אושר", color: "green" },
    rejected: { label: "נדחה", color: "red" },
    archived: { label: "בארכיון", color: "blue" },
  };
  const { label, color } = map[status];
  return <Badge color={color} variant="light">{label}</Badge>;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function formatTimeAgo(iso: string): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return iso;
  const ago = Math.max(0, Date.now() - t);
  const min = Math.floor(ago / 60_000);
  if (min < 1) return "עכשיו";
  if (min < 60) return `לפני ${min} דק'`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `לפני ${hr} שע'`;
  const d = Math.floor(hr / 24);
  return `לפני ${d} ימים`;
}
